// Shared geometries, materials, baked sub-meshes, and scatter-config types for
// TerrainScatter. Module-level singletons created once at load — safe because
// JS is single-threaded and the instanced-mesh effects only read them.
//
// Props use MeshBasicMaterial (#122, audit 2026-07-16: PBR shading crushed them
// to black at the dim day-night ambient floor), so there is no lighting. Depth
// is faked instead: every foliage/bark geometry carries baked vertex colours —
// a vertical gradient (darker underside, lighter crown) times a per-face tone
// jitter that reads as leaf/bark texture. The per-instance colour (see
// TerrainScatter's palettes) multiplies on top, so one geometry serves every
// tint.

import * as THREE from 'three';

// --- Vertex-colour "texture" baking ---

/** Deterministic [0,1) hash for bake-time jitter (no Math.random: stable across loads). */
function bakeHash(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Bake a shading gradient + faceted tone jitter into a geometry's vertex
 * colours. Non-indexed so each face gets one flat tone (low-poly "texture").
 * `bottom`/`top` are brightness multipliers at the geometry's min/max Y.
 */
export function bakeShade(
  geometry: THREE.BufferGeometry,
  { bottom, top, jitter, seed }: { bottom: number; top: number; jitter: number; seed: number },
): THREE.BufferGeometry {
  const geo = geometry.index ? geometry.toNonIndexed() : geometry;
  geo.computeBoundingBox();
  const minY = geo.boundingBox?.min.y ?? 0;
  const spanY = Math.max(1e-6, (geo.boundingBox?.max.y ?? 1) - minY);
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  for (let v = 0; v < pos.count; v += 3) {
    const faceTone = 1 + (bakeHash(seed + v) * 2 - 1) * jitter;
    for (let k = 0; k < 3 && v + k < pos.count; k++) {
      const t = (pos.getY(v + k) - minY) / spanY;
      const shade = (bottom + (top - bottom) * t) * faceTone;
      colors.set([shade, shade, shade], (v + k) * 3);
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  return geo;
}

/** Merge baked parts into one geometry so a species stays one draw call per part. */
export function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const count = parts.reduce((n, g) => n + g.getAttribute('position').count, 0);
  const position = new Float32Array(count * 3);
  const color = new Float32Array(count * 3);
  let offset = 0;
  for (const g of parts) {
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    const c = g.getAttribute('color') as THREE.BufferAttribute;
    position.set(p.array as Float32Array, offset * 3);
    color.set(c.array as Float32Array, offset * 3);
    offset += p.count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(position, 3));
  out.setAttribute('color', new THREE.BufferAttribute(color, 3));
  out.computeVertexNormals();
  return out;
}

const FOLIAGE = { bottom: 0.72, top: 1.1, jitter: 0.14 };
const BARK = { bottom: 0.7, top: 1.0, jitter: 0.18 };

// --- Tree species (foliage + trunk, local transforms baked in) ---

// Pine: two stacked cones, broad base.
const pineFoliageGeo = merge([
  bakeShade(new THREE.ConeGeometry(0.27, 0.46, 7).translate(0, 0.38, 0), { ...FOLIAGE, seed: 1 }),
  bakeShade(new THREE.ConeGeometry(0.2, 0.4, 7).translate(0, 0.64, 0), { ...FOLIAGE, seed: 2 }),
]);
const pineTrunkGeo = bakeShade(
  new THREE.CylinderGeometry(0.045, 0.07, 0.3, 5).translate(0, 0.15, 0),
  {
    ...BARK,
    seed: 3,
  },
);

// Spruce: tall, narrow, three tiers.
const spruceFoliageGeo = merge([
  bakeShade(new THREE.ConeGeometry(0.21, 0.42, 6).translate(0, 0.36, 0), { ...FOLIAGE, seed: 4 }),
  bakeShade(new THREE.ConeGeometry(0.17, 0.38, 6).translate(0, 0.6, 0), { ...FOLIAGE, seed: 5 }),
  bakeShade(new THREE.ConeGeometry(0.12, 0.34, 6).translate(0, 0.83, 0), { ...FOLIAGE, seed: 6 }),
]);
const spruceTrunkGeo = bakeShade(
  new THREE.CylinderGeometry(0.04, 0.06, 0.3, 5).translate(0, 0.15, 0),
  {
    ...BARK,
    seed: 7,
  },
);

// Oak: clustered low-poly crown on a thick trunk.
const oakFoliageGeo = merge([
  bakeShade(new THREE.IcosahedronGeometry(0.24, 0).translate(0, 0.62, 0), { ...FOLIAGE, seed: 8 }),
  bakeShade(new THREE.IcosahedronGeometry(0.19, 0).translate(0.16, 0.54, 0.05), {
    ...FOLIAGE,
    seed: 9,
  }),
  bakeShade(new THREE.IcosahedronGeometry(0.18, 0).translate(-0.14, 0.56, -0.06), {
    ...FOLIAGE,
    seed: 10,
  }),
  bakeShade(new THREE.IcosahedronGeometry(0.16, 0).translate(0.02, 0.78, 0.02), {
    ...FOLIAGE,
    seed: 11,
  }),
]);
const oakTrunkGeo = bakeShade(
  new THREE.CylinderGeometry(0.06, 0.09, 0.48, 6).translate(0, 0.24, 0),
  {
    ...BARK,
    seed: 12,
  },
);

// Birch: slim pale trunk, small high oval crown.
const birchFoliageGeo = merge([
  bakeShade(new THREE.IcosahedronGeometry(0.16, 0).scale(1, 1.35, 1).translate(0, 0.72, 0), {
    ...FOLIAGE,
    seed: 13,
  }),
  bakeShade(new THREE.IcosahedronGeometry(0.11, 0).translate(0.09, 0.56, 0.03), {
    ...FOLIAGE,
    seed: 14,
  }),
]);
const birchTrunkGeo = bakeShade(
  new THREE.CylinderGeometry(0.03, 0.045, 0.66, 5).translate(0, 0.33, 0),
  {
    bottom: 0.75,
    top: 1.0,
    jitter: 0.3, // high jitter = the dark bark flecks on white birch
    seed: 15,
  },
);

// Bush: low, wide clump; no trunk.
const bushGeo = merge([
  bakeShade(new THREE.IcosahedronGeometry(0.16, 0).scale(1.3, 0.8, 1.2).translate(0, 0.11, 0), {
    ...FOLIAGE,
    seed: 16,
  }),
  bakeShade(new THREE.IcosahedronGeometry(0.11, 0).translate(0.12, 0.09, 0.04), {
    ...FOLIAGE,
    seed: 17,
  }),
]);

// --- Jungle species (tropical climate, hot and wet) ---

const JUNGLE = { bottom: 0.7, top: 1.12, jitter: 0.16 };

// Palm: a slim leaning trunk under a ring of six drooping fronds.
const palmTrunkGeo = bakeShade(
  new THREE.CylinderGeometry(0.03, 0.05, 0.62, 5).translate(0, 0.31, 0),
  { ...BARK, seed: 31 },
);
const palmFrondGeos: THREE.BufferGeometry[] = [];
for (let i = 0; i < 6; i++) {
  const a = (i / 6) * Math.PI * 2;
  palmFrondGeos.push(
    bakeShade(
      new THREE.ConeGeometry(0.045, 0.34, 4)
        .rotateZ(Math.PI / 2)
        .translate(0.17, 0, 0)
        .rotateZ(-0.5)
        .rotateY(a)
        .translate(0, 0.66, 0),
      { ...JUNGLE, seed: 32 + i },
    ),
  );
}
const palmFoliageGeo = merge(palmFrondGeos);

// Jungle tree: tall trunk with buttress roots under a broad, flat umbrella canopy of stacked lobes.
const jungleTrunkGeo = merge([
  bakeShade(new THREE.CylinderGeometry(0.05, 0.085, 0.8, 6).translate(0, 0.4, 0), {
    ...BARK,
    seed: 41,
  }),
  bakeShade(new THREE.ConeGeometry(0.13, 0.22, 5).translate(0, 0.11, 0), { ...BARK, seed: 42 }),
]);
const jungleFoliageGeo = merge([
  bakeShade(new THREE.IcosahedronGeometry(0.3, 0).scale(1.25, 0.5, 1.25).translate(0, 0.86, 0), {
    ...JUNGLE,
    seed: 43,
  }),
  bakeShade(
    new THREE.IcosahedronGeometry(0.2, 0).scale(1.2, 0.55, 1.2).translate(0.2, 0.74, 0.06),
    {
      ...JUNGLE,
      seed: 44,
    },
  ),
  bakeShade(
    new THREE.IcosahedronGeometry(0.19, 0).scale(1.2, 0.55, 1.2).translate(-0.18, 0.76, -0.08),
    { ...JUNGLE, seed: 45 },
  ),
  bakeShade(
    new THREE.IcosahedronGeometry(0.16, 0).scale(1.1, 0.6, 1.1).translate(0.02, 1.0, 0.03),
    {
      ...JUNGLE,
      seed: 46,
    },
  ),
]);

// Fern: a fan of five flat leaves, the jungle floor's undergrowth.
const fernLeafGeos: THREE.BufferGeometry[] = [];
for (let i = 0; i < 5; i++) {
  const a = (i / 5) * Math.PI * 2;
  fernLeafGeos.push(
    bakeShade(
      new THREE.ConeGeometry(0.035, 0.22, 3)
        .rotateZ(Math.PI / 2)
        .translate(0.1, 0, 0)
        .rotateZ(0.7)
        .rotateY(a)
        .translate(0, 0.02, 0),
      { ...JUNGLE, seed: 51 + i },
    ),
  );
}
const fernGeo = merge(fernLeafGeos);

// --- Other props ---

const rockGeo = bakeShade(new THREE.IcosahedronGeometry(0.15, 0), {
  bottom: 0.7,
  top: 1.05,
  jitter: 0.12,
  seed: 18,
});
// Flower: thin stem (green, via the trunk palette) under a small three-blob
// blossom (instance-tinted pastel). Sits in the grass layer height-wise.
const flowerStemGeo = bakeShade(
  new THREE.CylinderGeometry(0.012, 0.018, 0.16, 5).translate(0, 0.08, 0),
  { bottom: 0.75, top: 1.0, jitter: 0.15, seed: 19 },
);
const flowerBlossomGeo = merge([
  bakeShade(new THREE.IcosahedronGeometry(0.05, 0).translate(0, 0.18, 0), {
    bottom: 0.85,
    top: 1.15,
    jitter: 0.2,
    seed: 20,
  }),
  bakeShade(new THREE.IcosahedronGeometry(0.032, 0).translate(0.05, 0.14, 0.03), {
    bottom: 0.85,
    top: 1.15,
    jitter: 0.2,
    seed: 21,
  }),
  bakeShade(new THREE.IcosahedronGeometry(0.03, 0).translate(-0.04, 0.13, -0.02), {
    bottom: 0.85,
    top: 1.15,
    jitter: 0.2,
    seed: 22,
  }),
]);
const grassGeo = new THREE.PlaneGeometry(0.04, 0.18);
const cactusGeo = new THREE.CylinderGeometry(0.06, 0.08, 0.35, 8);
const snowBoulderGeo = new THREE.SphereGeometry(0.18, 6, 4);

// --- Materials ---
// White base + vertexColors: the baked shade and the per-instance colour
// (instanceColor) supply all of the tint.

const shadedMat = (): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)', vertexColors: true });

// --- Wind sway ---
// Shared time uniform, advanced by WindAnimator (TerrainScatter.tsx). Sway is
// a vertex-shader bend that grows with local height squared, so blades pivot
// at the ground and foliage crowns lean while their bases stay planted. The
// phase comes from the instance origin's world XZ, so neighbours drift out of
// step instead of pulsing in sync. Trunks (barkMat) and rocks stay rigid.
export const windTime = { value: 0 };

function applyWindSway(
  mat: THREE.MeshBasicMaterial,
  { amp, minY, spanY }: { amp: number; minY: number; spanY: number },
): void {
  const f = (v: number) => v.toFixed(5);
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWindTime = windTime;
    shader.vertexShader = `uniform float uWindTime;\n${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      #ifdef USE_INSTANCING
      {
        float _ph = instanceMatrix[3][0] * 1.9 + instanceMatrix[3][2] * 1.4;
        float _h = clamp((transformed.y - ${f(minY)}) / ${f(spanY)}, 0.0, 1.0);
        float _sway = (sin(uWindTime * 1.8 + _ph) + 0.45 * sin(uWindTime * 3.1 + _ph * 1.7 + 1.3))
          * ${f(amp)} * _h * _h;
        transformed.x += _sway;
        transformed.z += 0.7 * _sway;
      }
      #endif`,
    );
  };
  // Unique per config: three's default program cache key is the *source* of
  // onBeforeCompile, and the closure constants don't show up in it — without
  // this, grass and foliage would share whichever program compiled first.
  mat.customProgramCacheKey = () => `scatter-wind:${amp}:${minY}:${spanY}`;
}

export const foliageMat = shadedMat();
export const barkMat = shadedMat();
export const rockMat = shadedMat();
// Grass blades (local y 0..0.18) flutter at the tip; foliage crowns
// (~y 0.25..0.9) breathe. Kept subtle — the board must stay readable.
applyWindSway(foliageMat, { amp: 0.02, minY: 0.25, spanY: 0.65 });
export const grassMat = new THREE.MeshBasicMaterial({
  color: 'rgb(255,255,255)',
  side: THREE.DoubleSide,
});
applyWindSway(grassMat, { amp: 0.026, minY: 0.0, spanY: 0.18 });
// Blossoms flutter a touch less than grass blades (they sit higher, so the
// same amplitude would read bigger).
export const flowerMat = shadedMat();
applyWindSway(flowerMat, { amp: 0.02, minY: 0.05, spanY: 0.2 });
export const cactusMat = new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)' });
export const snowBoulderMat = new THREE.MeshBasicMaterial({ color: 'rgb(245,250,255)' });

// --- Sub-mesh geometries with local transforms baked in ---
// Fixed-offset children (cactus arms, grass blade rotations) live in vertex
// positions so the per-instance matrix only carries world translate/rotate/scale.

export const cactusArmLBakedGeo = new THREE.CylinderGeometry(0.04, 0.05, 0.2, 6)
  .rotateZ(Math.PI / 3)
  .translate(0.08, 0.15, 0);
export const cactusArmRBakedGeo = new THREE.CylinderGeometry(0.04, 0.05, 0.2, 6)
  .rotateZ(-Math.PI / 3)
  .translate(-0.08, 0.1, 0);
export const grassABakedGeo = grassGeo.clone().rotateZ(0.1).translate(0, 0.09, 0);
export const grassBBakedGeo = grassGeo
  .clone()
  .rotateY(Math.PI / 3)
  .rotateZ(0.1)
  .translate(0, 0.09, 0);
export const grassCBakedGeo = grassGeo
  .clone()
  .rotateY(-Math.PI / 3)
  .rotateZ(0.1)
  .translate(0, 0.09, 0);

export type TreeSpecies =
  | 'pine'
  | 'spruce'
  | 'oak'
  | 'birch'
  | 'bush'
  | 'palm'
  | 'jungleTree'
  | 'fern';
export type ScatterPropType = TreeSpecies | 'rock' | 'grass' | 'flower' | 'cactus' | 'snowBoulder';

/** Which palette tints a sub-mesh: the prop's main colour, or its trunk. */
export type SubMeshRole = 'main' | 'trunk';

export interface ScatterSubMesh {
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.Material;
  readonly role: SubMeshRole;
}

export const PROP_SUBMESHES: Record<ScatterPropType, readonly ScatterSubMesh[]> = {
  pine: [
    { geometry: pineTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: pineFoliageGeo, material: foliageMat, role: 'main' },
  ],
  spruce: [
    { geometry: spruceTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: spruceFoliageGeo, material: foliageMat, role: 'main' },
  ],
  oak: [
    { geometry: oakTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: oakFoliageGeo, material: foliageMat, role: 'main' },
  ],
  birch: [
    { geometry: birchTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: birchFoliageGeo, material: foliageMat, role: 'main' },
  ],
  bush: [{ geometry: bushGeo, material: foliageMat, role: 'main' }],
  palm: [
    { geometry: palmTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: palmFoliageGeo, material: foliageMat, role: 'main' },
  ],
  jungleTree: [
    { geometry: jungleTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: jungleFoliageGeo, material: foliageMat, role: 'main' },
  ],
  fern: [{ geometry: fernGeo, material: foliageMat, role: 'main' }],
  rock: [{ geometry: rockGeo, material: rockMat, role: 'main' }],
  grass: [
    { geometry: grassABakedGeo, material: grassMat, role: 'main' },
    { geometry: grassBBakedGeo, material: grassMat, role: 'main' },
    { geometry: grassCBakedGeo, material: grassMat, role: 'main' },
  ],
  flower: [
    { geometry: flowerStemGeo, material: barkMat, role: 'trunk' },
    { geometry: flowerBlossomGeo, material: flowerMat, role: 'main' },
  ],
  cactus: [
    { geometry: cactusGeo, material: cactusMat, role: 'main' },
    { geometry: cactusArmLBakedGeo, material: cactusMat, role: 'main' },
    { geometry: cactusArmRBakedGeo, material: cactusMat, role: 'main' },
  ],
  snowBoulder: [{ geometry: snowBoulderGeo, material: snowBoulderMat, role: 'main' }],
};

/** A weighted species pick for a scatter config (e.g. forest = pine/spruce/oak/birch/bush). */
export interface WeightedProp {
  readonly propType: ScatterPropType;
  readonly weight: number;
}

export interface ScatterConfig {
  /** Stable id: salts the per-tile hashes so configs sharing a biome don't overlap. */
  id: string;
  biomes: string[];
  /** One entry = a single prop type; several = a seeded weighted mix per instance. */
  props: readonly WeightedProp[];
  minCount: number;
  maxCount: number;
  castShadow: boolean;
  yOffset: number;
  /** Instance size multiplier (default 1) — boulders up-size the same rock geometry. */
  scale?: number;
  /**
   * Cluster instances into per-tile patches (grass tufts) instead of
   * scattering them uniformly across the tile.
   */
  patchy?: boolean;
  /**
   * Pick the instance colour per instance (hash) instead of walking the
   * shared low-frequency field — wildflowers mix hues within a patch,
   * while grove-coherent props (trees) drift together.
   */
  perInstanceColor?: boolean;
  /** Skip entirely during winter (seasonDay active) — e.g. flowers. */
  hideInWinter?: boolean;
  /** Only drawn on maps of these climates (absent = every climate). */
  climates?: readonly string[];
  /** Not drawn on maps of these climates (the jungle replaces the temperate forest). */
  exceptClimates?: readonly string[];
}
